import { useState, useEffect, useMemo, useCallback } from "react"
import {
  useGetBundleColumnsQuery,
  useUpdateBundleColumnsMutation,
  useApplyColumnTemplateMutation,
  useGetColumnTemplatesQuery,
  useGetColumnTemplateDepartmentsQuery,
  useCreateColumnTemplateMutation,
  useUpdateColumnTemplateMutation,
  useDeleteColumnTemplateMutation,
} from "../../../../app/services/book/bundle"

export function useColumnSettings() {
  const { data: serverColumns, isSuccess, isLoading } = useGetBundleColumnsQuery()

  // Два разных запроса на бэке: update_columns сохраняет только личный
  // порядок, apply_template — применяет шаблон (id + сброс личного оверрайда).
  // Раньше это был один updateBundleColumns на всё — из-за этого личный
  // порядок и выбор шаблона затирали друг друга.
  const [updateBundleColumns] = useUpdateBundleColumnsMutation()
  const [applyColumnTemplateMutation] = useApplyColumnTemplateMutation()

  const [colOrder, setColOrder] = useState([])
  const [visibleCols, setVisibleCols] = useState([])
  const [appliedTemplateId, setAppliedTemplateId] = useState(null)

  useEffect(() => {
    if (!isSuccess || !serverColumns) return
    setColOrder(
      serverColumns.order?.length ? serverColumns.order : serverColumns.available.map((c) => c.id)
    )
    setVisibleCols(serverColumns.active?.length ? serverColumns.active : serverColumns.defaults)
    setAppliedTemplateId(serverColumns.applied_template_id ?? null)
  }, [isSuccess, serverColumns])

  const columns = useMemo(() => serverColumns?.available ?? [], [serverColumns])

  const { data: templates = [], isLoading: templatesLoading } = useGetColumnTemplatesQuery()
  // Реальный список департаментов с бэка — не зависит от того, есть ли
  // у департамента уже созданные шаблоны.
  const { data: departments = [] } = useGetColumnTemplateDepartmentsQuery()

  const [createTemplateMutation] = useCreateColumnTemplateMutation()
  const [updateTemplateMutation] = useUpdateColumnTemplateMutation()
  const [deleteTemplateMutation] = useDeleteColumnTemplateMutation()

  // Личный порядок (drag-n-drop на вкладке "Колонки"). Видимость сюда не
  // передаём — она теперь управляется только шаблоном, а не этим запросом.
  // appliedTemplateId не трогаем: перестановка колонок не отменяет
  // применённый шаблон, она просто накладывается поверх него.
  const applyColumnSettings = useCallback(
    async (order) => {
      setColOrder(order)
      try {
        await updateBundleColumns({ order }).unwrap()
      } catch (e) {
        console.error("Ошибка сохранения порядка колонок", e)
        throw e
      }
    },
    [updateBundleColumns]
  )

  // Применение шаблона. visible/order на бэк не шлём — сервер сам берёт их
  // из шаблона и сбрасывает личный оверрайд порядка. Локально проставляем
  // то же самое, чтобы не ждать перезапроса GET /columns.
  const applyTemplate = useCallback(
    async (template) => {
      setColOrder(template.order)
      setVisibleCols(template.visible)
      setAppliedTemplateId(template.id)
      try {
        await applyColumnTemplateMutation({ templateId: template.id }).unwrap()
      } catch (e) {
        console.error("Ошибка применения шаблона колонок", e)
        throw e
      }
    },
    [applyColumnTemplateMutation]
  )

  const createTemplate = useCallback(
    async (data) => {
      try {
        return await createTemplateMutation(data).unwrap()
      } catch (e) {
        console.error("Ошибка создания шаблона", e)
        throw e
      }
    },
    [createTemplateMutation]
  )

  const updateTemplate = useCallback(
    async (id, data) => {
      try {
        await updateTemplateMutation({ id, ...data }).unwrap()
      } catch (e) {
        console.error("Ошибка обновления шаблона", e)
        throw e
      }
    },
    [updateTemplateMutation]
  )

  const deleteTemplate = useCallback(
    async (id) => {
      try {
        await deleteTemplateMutation(id).unwrap()
        if (String(appliedTemplateId) === String(id)) setAppliedTemplateId(null)
      } catch (e) {
        console.error("Ошибка удаления шаблона", e)
        throw e
      }
    },
    [deleteTemplateMutation, appliedTemplateId]
  )

  return {
    columns,
    colOrder,
    visibleCols,
    applyColumnSettings,
    isLoading,
    templates,
    templatesLoading,
    departments,
    appliedTemplateId,
    applyTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  }
}
